import { styled, css } from 'next-yak'
import * as React from 'react'

import * as breakpoints from '../constants/breakpoints'
import * as colors from '../constants/colors'
import * as fonts from '../constants/fonts'
import * as typograhpy from '../constants/typography'
import {
  Polymorphic,
  type PolymorphicComponent,
  type YakComponentOf,
  withPrivatePublicMap,
} from '@owl-nest/yak/extra-runtime'

export const _baseStyle = css`
  color: ${colors.PRIMARY_GREY_900};
  font-family: ${fonts.PRIMARY_FONT};
  margin: 0;
  padding: 0;
`

export type Size = 'XXXXXXS' | 'XXXXXS' | 'XXXXS' | 'XXXS' | 'XXS' | 'XS' | 'S' | 'M' | 'L' | 'XL' | 'XXL' | 'XXXL'

export const styles = {
  XXXXXXS: css`
    font-size: ${typograhpy.HEADING_6XS.fontSize};
    line-height: ${typograhpy.HEADING_6XS.lineHeight};
    font-weight: ${typograhpy.HEADING_6XS.fontWeight};
  `,
  XXXXXS: css`
    font-size: ${typograhpy.HEADING_5XS.fontSize};
    line-height: ${typograhpy.HEADING_5XS.lineHeight};
    font-weight: ${typograhpy.HEADING_5XS.fontWeight};
  `,
  XXXXS: css`
    font-size: ${typograhpy.HEADING_4XS.fontSize};
    line-height: ${typograhpy.HEADING_4XS.lineHeight};
    font-weight: ${typograhpy.HEADING_4XS.fontWeight};
  `,
  XXXS: css`
    font-size: ${typograhpy.HEADING_3XS.fontSize};
    line-height: ${typograhpy.HEADING_3XS.lineHeight};
    font-weight: ${typograhpy.HEADING_3XS.fontWeight};
  `,
  XXS: css`
    font-size: ${typograhpy.HEADING_XXS.fontSize};
    line-height: ${typograhpy.HEADING_XXS.lineHeight};
    font-weight: ${typograhpy.HEADING_XXS.fontWeight};
  `,
  XS: css`
    font-size: ${typograhpy.HEADING_XS.fontSize};
    line-height: ${typograhpy.HEADING_XS.lineHeight};
    font-weight: ${typograhpy.HEADING_XS.fontWeight};
  `,
  S: css`
    font-size: ${typograhpy.HEADING_S.fontSize};
    line-height: ${typograhpy.HEADING_S.lineHeight};
    font-weight: ${typograhpy.HEADING_S.fontWeight};
  `,
  M: css`
    font-size: ${typograhpy.HEADING_M.fontSize};
    line-height: ${typograhpy.HEADING_M.lineHeight};
    font-weight: ${typograhpy.HEADING_M.fontWeight};
  `,
  L: css`
    font-size: ${typograhpy.HEADING_L.fontSize};
    line-height: ${typograhpy.HEADING_L.lineHeight};
    font-weight: ${typograhpy.HEADING_L.fontWeight};
  `,
  XL: css`
    font-size: ${typograhpy.HEADING_XL.fontSize};
    line-height: ${typograhpy.HEADING_XL.lineHeight};
    font-weight: ${typograhpy.HEADING_XL.fontWeight};
  `,
  XXL: css`
    font-size: ${typograhpy.HEADING_XXL.fontSize};
    line-height: ${typograhpy.HEADING_XXL.lineHeight};
    font-weight: ${typograhpy.HEADING_XXL.fontWeight};
  `,
  XXXL: css`
    font-size: ${typograhpy.HEADING_3XL.fontSize};
    line-height: ${typograhpy.HEADING_3XL.lineHeight};
    font-weight: ${typograhpy.HEADING_3XL.fontWeight};
  `,
} satisfies Record<Size, unknown>

export const Base = styled(Polymorphic.div)`
  ${_baseStyle}
` as YakComponentOf<PolymorphicComponent<'div'>>

type VariantProps = {
  $size?: Size
}

const dynamicHeading = css<VariantProps>`
  ${({ $size }) => {
    // can't use `styles[$size]` because yak can't parse member
    // expression and infer what styles are possible. We need to explicitly
    // return each possible style with nested ifs.
    if ($size === 'XXXXXXS') {
      return styles.XXXXXXS
    }
    if ($size === 'XXXXXS') {
      return styles.XXXXXS
    }
    if ($size === 'XXXXS') {
      return styles.XXXXS
    }
    if ($size === 'XXXS') {
      return styles.XXXS
    }
    if ($size === 'XXS') {
      return styles.XXS
    }
    if ($size === 'XS') {
      return styles.XS
    }
    if ($size === 'S') {
      return styles.S
    }
    if ($size === 'M') {
      return styles.M
    }
    if ($size === 'L') {
      return styles.L
    }
    if ($size === 'XL') {
      return styles.XL
    }
    if ($size === 'XXL') {
      return styles.XXL
    }
    if ($size === 'XXXL') {
      return styles.XXXL
    }
  }}
`

type BaseResponsiveProps = VariantProps & {
  $breakpoint?: 'mobile-s' | 'mobile-m' | 'mobile-l' | 'tablet' | 'laptop' | 'laptop-l' | 'desktop'
}

const baseResponsiveStyle = css<BaseResponsiveProps>`
  ${({ $breakpoint = 'tablet' }) => {
    if ($breakpoint === 'mobile-s') {
      return css<VariantProps>`
        @media screen and ${breakpoints.MOBILE_S} {
          ${dynamicHeading}
        }
      `
    }
    if ($breakpoint === 'mobile-m') {
      return css<VariantProps>`
        @media screen and ${breakpoints.MOBILE_M} {
          ${dynamicHeading}
        }
      `
    }
    if ($breakpoint === 'mobile-l') {
      return css<VariantProps>`
        @media screen and ${breakpoints.MOBILE_L} {
          ${dynamicHeading}
        }
      `
    }
    if ($breakpoint === 'tablet') {
      return css<VariantProps>`
        @media screen and ${breakpoints.TABLET} {
          ${dynamicHeading}
        }
      `
    }
    if ($breakpoint === 'laptop') {
      return css<VariantProps>`
        @media screen and ${breakpoints.LAPTOP} {
          ${dynamicHeading}
        }
      `
    }
    if ($breakpoint === 'laptop-l') {
      return css<VariantProps>`
        @media screen and ${breakpoints.LAPTOP_L} {
          ${dynamicHeading}
        }
      `
    }
    if ($breakpoint === 'desktop') {
      return css<VariantProps>`
        @media screen and ${breakpoints.DESKTOP} {
          ${dynamicHeading}
        }
      `
    }
  }}
`

export type HeadingProps = {
  asDesktop?: Size
  breakpoint?: 'mobile-s' | 'mobile-m' | 'mobile-l' | 'tablet' | 'laptop' | 'laptop-l' | 'desktop'
}

const privateXXXL = styled(Base)<BaseResponsiveProps>`
  ${styles.XXXL};
  ${baseResponsiveStyle};
`

export const XXXL = styled(withPrivatePublicMap(privateXXXL, privatePublicMap))`` as YakComponentOf<
  PolymorphicComponent<'div', HeadingProps>
>
XXXL.displayName = 'heading.XXXL'

const privateXXL = styled(Base)<BaseResponsiveProps>`
  ${styles.XXL};
  ${baseResponsiveStyle};
`

export const XXL = styled(withPrivatePublicMap(privateXXL, privatePublicMap))`` as YakComponentOf<
  PolymorphicComponent<'div', HeadingProps>
>
XXL.displayName = 'heading.XXL'

const privateXL = styled(Base)<BaseResponsiveProps>`
  ${styles.XL};
  ${baseResponsiveStyle};
`

export const XL = styled(withPrivatePublicMap(privateXL, privatePublicMap))`` as YakComponentOf<
  PolymorphicComponent<'div', HeadingProps>
>
XL.displayName = 'heading.XL'

const privateL = styled(Base)<BaseResponsiveProps>`
  ${styles.L};
  ${baseResponsiveStyle};
`

export const L = styled(withPrivatePublicMap(privateL, privatePublicMap))`` as YakComponentOf<
  PolymorphicComponent<'div', HeadingProps>
>
L.displayName = 'heading.L'

const privateM = styled(Base)<BaseResponsiveProps>`
  ${styles.M};
  ${baseResponsiveStyle};
`

export const M = styled(withPrivatePublicMap(privateM, privatePublicMap))`` as YakComponentOf<
  PolymorphicComponent<'div', HeadingProps>
>
M.displayName = 'heading.M'

const privateS = styled(Base)<BaseResponsiveProps>`
  ${styles.S};
  ${baseResponsiveStyle};
`

export const S = styled(withPrivatePublicMap(privateS, privatePublicMap))`` as YakComponentOf<
  PolymorphicComponent<'div', HeadingProps>
>
S.displayName = 'heading.S'

const privateXS = styled(Base)<BaseResponsiveProps>`
  ${styles.XS};
  ${baseResponsiveStyle};
`

export const XS = styled(withPrivatePublicMap(privateXS, privatePublicMap))`` as YakComponentOf<
  PolymorphicComponent<'div', HeadingProps>
>
XS.displayName = 'heading.XS'

const privateXXS = styled(Base)<BaseResponsiveProps>`
  ${styles.XXS};
  ${baseResponsiveStyle};
`

export const XXS = styled(withPrivatePublicMap(privateXXS, privatePublicMap))`` as YakComponentOf<
  PolymorphicComponent<'div', HeadingProps>
>
XXS.displayName = 'heading.XXS'

const privateXXXS = styled(Base)<BaseResponsiveProps>`
  ${styles.XXXS};
  ${baseResponsiveStyle};
`

export const XXXS = styled(withPrivatePublicMap(privateXXXS, privatePublicMap))`` as YakComponentOf<
  PolymorphicComponent<'div', HeadingProps>
>
XXXS.displayName = 'heading.XXXS'

const privateXXXXS = styled(Base)<BaseResponsiveProps>`
  ${styles.XXXXS};
  ${baseResponsiveStyle};
`

export const XXXXS = styled(withPrivatePublicMap(privateXXXXS, privatePublicMap))`` as YakComponentOf<
  PolymorphicComponent<'div', HeadingProps>
>
XXXXS.displayName = 'heading.XXXXS'

const privateXXXXXS = styled(Base)<BaseResponsiveProps>`
  ${styles.XXXXXS};
  ${baseResponsiveStyle};
`

export const XXXXXS = styled(withPrivatePublicMap(privateXXXXXS, privatePublicMap))`` as YakComponentOf<
  PolymorphicComponent<'div', HeadingProps>
>
XXXXXS.displayName = 'heading.XXXXXS'

const privateXXXXXXS = styled(Base)<BaseResponsiveProps>`
  ${styles.XXXXXXS};
  ${baseResponsiveStyle};
`

export const XXXXXXS = styled(withPrivatePublicMap(privateXXXXXXS, privatePublicMap))`` as YakComponentOf<
  PolymorphicComponent<'div', HeadingProps>
>
XXXXXXS.displayName = 'heading.XXXXXXS'

function privatePublicMap({ asDesktop, breakpoint, ...props }: HeadingProps) {
  return {
    ...props,
    $size: asDesktop,
    $breakpoint: breakpoint,
  }
}

type HighlightColor = 'blue' | 'green' | 'red'

export type HighlightedTitleProps = {
  color?: HighlightColor
  small?: boolean
}

type PrivateHighlightedTitleProps = { [K in keyof HighlightedTitleProps as `$${K}`]: HighlightedTitleProps[K] }

const HighlightedTitleBase = styled.span<PrivateHighlightedTitleProps>`
  display: inline-block;

  ${({ $color, theme }) => {
    if ($color === 'blue') {
      return css`
        background-color: ${colors.PRIMARY_BLUE_100};
        color: ${colors.PRIMARY_BLUE_700};
      `
    }
    if ($color === 'green') {
      return css`
        background-color: ${colors.SECONDARY_MINT_300};
        color: ${colors.SECONDARY_MINT_700};
      `
    }
    if ($color === 'red') {
      return css`
        background-color: ${colors.SECONDARY_RED_200};
        color: ${colors.PRIMARY_RED_500};
      `
    } else if ((theme as any)?.colors?.background && (theme as any)?.colors?.primary) {
      return css`
        background-color: ${(theme as any)?.colors?.background};
        color: ${(theme as any)?.colors?.primary};
      `
    } else {
      return css`
        background-color: ${colors.PRIMARY_SAND_100};
        color: ${colors.PRIMARY_GREY_900};
      `
    }
  }}

  ${({ $small }) =>
    $small
      ? css`
          margin-bottom: -10px;
          padding: 4px 6px 6px;

          @media screen and ${breakpoints.LAPTOP} {
            margin-bottom: -10px;
            padding: 1px 6px 8px;
          }
        `
      : css`
          margin-bottom: -6px;
          padding: 4px 6px 6px;

          @media screen and ${breakpoints.LAPTOP} {
            margin-bottom: 0;
            padding: 1px 6px 8px;
          }
        `}
`

export const HighlightedTitle = styled(
  ({ color, small, ...props }: HighlightedTitleProps & React.JSX.IntrinsicElements['span']) => {
    return <HighlightedTitleBase {...props} $color={color} $small={small} />
  },
)``
